import { useColorScheme } from '@mui/material/styles';
import type { AgChartOptions } from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';
import type { AgCartesianAxesOptions } from 'ag-charts-types';
import { useCallback, useMemo } from 'react';

import {
  getAgChartsTheme,
  noDataRenderer
} from '@/components/charts/charts.utils';
import {
  formatLargeNumber,
  hasDataGreaterThanZero
} from '@/utils/Common.utils';

import { useChartLocationCheck } from '../charts.hooks';
import useStyles from './StackedBarChart.styles';
import type { StackedBarChartProps } from './StackedBarChart.types';

const StackedBarChart = (props: StackedBarChartProps) => {
  const { data, series, handleChartClick, outlined } = props;
  const { mode: themeMode } = useColorScheme();
  const { classes } = useStyles({ outlined });
  const onReportsPage = useChartLocationCheck();
  const hasVisibleData = useMemo(
    () => hasDataGreaterThanZero(data || []),
    [data]
  );

  const numFormatter = new Intl.NumberFormat('en-US');

  const getFormattedAxes = useCallback((): AgCartesianAxesOptions => {
    if (!data?.length || !series?.length) {
      return {
        x: {
          type: 'category',
          position: 'bottom',
          tick: {
            enabled: false
          },
          line: {
            enabled: false
          }
        },
        y: {
          type: 'number',
          position: 'left',
          title: {
            enabled: false
          },
          line: {
            enabled: false
          }
        }
      };
    }

    return {
      x: {
        type: 'unit-time',
        position: 'bottom',
        label: {
          autoRotate: true,
          format: '%b %d',
          color: onReportsPage ? 'black' : undefined
        },
        tick: {
          enabled: true
        },
        line: {
          enabled: hasVisibleData
        }
      },
      y: {
        type: 'number',
        position: 'left',
        label: {
          color: onReportsPage ? 'black' : undefined,
          formatter: (params: { value: number }) =>
            formatLargeNumber(params.value)
        },
        line: {
          enabled: hasVisibleData
        }
      }
    };
  }, [data, hasVisibleData, onReportsPage, series]);

  const options: AgChartOptions = useMemo(
    () => ({
      data: hasVisibleData ? data : [],
      series,
      background: {
        fill: 'transparent'
      },
      theme: getAgChartsTheme(themeMode),
      axes: getFormattedAxes(),
      legend: {
        enabled: hasVisibleData,
        spacing: 16,
        maxHeight: 1000,
        item: {
          label: {
            color: onReportsPage ? 'black' : undefined,
            formatter(params) {
              const sum = data?.reduce((total, item) => {
                return total + (item[params.itemId] || 0);
              }, 0);
              return `${params.value} - ${numFormatter.format(sum)}`;
            }
          }
        }
      },
      listeners: {
        seriesNodeClick: ({ datum, yKey }) => {
          handleChartClick?.(yKey as string, datum.date);
        }
      },
      padding: {
        bottom: 0,
        right: 28,
        top: 28
      },
      animation: {
        enabled: true
      },
      overlays: {
        noData: {
          renderer: () => noDataRenderer()
        }
      }
    }),
    [data, series, hasVisibleData, themeMode, handleChartClick]
  );

  return <AgCharts className={classes.stackedBarChart} options={options} />;
};

StackedBarChart.displayName = 'StackedBarChart';

export default StackedBarChart;
